// client/src/pages/seo/DigitalMenuForRestaurantsEn.jsx
// Route: /en/digital-menu-for-restaurants
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

const sections = [
  { id: "definition", label: "What is a digital menu?" },
  { id: "types", label: "What types of digital menus are there?" },
  { id: "criteria", label: "How can you recognize a good solution?" },
  { id: "workflow", label: "What changes in day-to-day restaurant operations?" },
  { id: "bestplace", label: "Digital menu with Best Place" },
  { id: "qr", label: "When does a digital menu need a QR code?" },
  { id: "faq", label: "Digital menu FAQ" },
];

function setMeta(name, content) {
  let tag = document.head.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export default function DigitalMenuForRestaurantsEn() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "en");
    document.title = "Digital Menu for Restaurants | Best Place";
    setMeta(
      "description",
      "Digital menu for restaurants: compare solutions, manage prices and dishes online, display the menu on mobile devices and choose the right provider."
    );
    setCanonical("https://best-place.club/en/digital-menu-for-restaurants");
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="en"
        langLinks={{
          en: "/en/digital-menu-for-restaurants",
          de: "/de/digitale-speisekarte",
          no: "/no/digital-meny-for-restauranter",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>Digital Menu for Restaurants</span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                A digital menu is an online-managed restaurant menu that guests can
                open on a smartphone, tablet or computer. For restaurant operations,
                what matters most is how quickly content can be updated and how well
                the menu works for guests.
              </p>
              <p className="seoHero__text">
                So when you search for a digital menu, the choice is not simply
                between paper and a smartphone. The real question is: which solution
                can be maintained reliably in everyday use, fits your restaurant and
                stays easy for guests?
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="en" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Contents</h2>
        <nav className="qrArticle__nav" aria-label="Article navigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">{String(index + 1).padStart(2, "0")}</span>
                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="definition" className="acquaintance">
          <h2>What is a digital menu?</h2>
          <p>
            A digital menu is not one specific technology. It first describes the
            result: a restaurant menu that is available online and can be maintained
            digitally.
          </p>
          <p>
            Guests can open it through a normal link, the restaurant website or a
            QR code. For the operator, the main difference from a printed menu is
            that prices, products, descriptions, images and availability can be
            changed without reprinting.
          </p>
          <p>
            That is exactly why choosing a solution should start with menu management,
            not with the QR code. A QR code is only one of several ways to access the
            digital menu.
          </p>
        </section>

        <section id="types" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What types of digital menus are there?</h2>
            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>1. PDF online</h3>
                <p>
                  The existing print file is made available online. This is simple,
                  but updates and mobile readability are often limited.
                </p>
              </div>
              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>2. Mobile web menu</h3>
                <p>
                  Categories, dishes and prices are maintained as a real mobile page.
                  Individual items can be updated independently.
                </p>
              </div>
              <div className="seoComparison__col">
                <h3>3. Menu plus ordering features</h3>
                <p>
                  Depending on the system, reservations, ordering, payment or other
                  restaurant functions can be connected in addition to menu display.
                </p>
              </div>
            </div>
            <p>
              For many restaurants, a mobile web menu is the flexible middle ground:
              easier to maintain than a PDF and less complex than a full ordering or
              POS system.
            </p>
          </div>
        </section>

        <section id="criteria" className="acquaintance">
          <h2>How can you recognize a good digital menu?</h2>
          <p>
            Good providers differ less by the promise of being “digital” and more
            by the quality of the solution in everyday restaurant operations.
          </p>
          <ul className="qrArticle__block-li">
            <li className="text__seo-li"><strong>Mobile display:</strong> clear categories, prices and images on small screens.</li>
            <li className="text__seo-li"><strong>Easy maintenance:</strong> staff can change dishes and prices themselves.</li>
            <li className="text__seo-li"><strong>Availability:</strong> quickly adjust sold-out or seasonal products.</li>
            <li className="text__seo-li"><strong>Multilingual support:</strong> important for international guests.</li>
            <li className="text__seo-li"><strong>Own public URL:</strong> menu accessible by link, website or QR code.</li>
            <li className="text__seo-li"><strong>Photos and descriptions:</strong> present products clearly.</li>
            <li className="text__seo-li"><strong>Price and limits:</strong> clearly understand which features are included in the plan.</li>
          </ul>
          <p>
            If you want to compare providers directly, use our dedicated page{" "}
            <Link to="/en/digital-menu-comparison">Digital Menu Comparison</Link>.
          </p>
        </section>

        <section id="workflow" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What changes in everyday restaurant operations with a digital menu?</h2>
            <div className="qrArticle__cardGrid">
              <div>
                <strong>Price changes</strong>
                <p>A price is changed once in the system and is then current online.</p>
              </div>
              <div>
                <strong>Sold-out products</strong>
                <p>Products can be adjusted or hidden before guests order.</p>
              </div>
              <div>
                <strong>Seasonal offers</strong>
                <p>Daily dishes and seasonal items do not have to wait for another print run.</p>
              </div>
              <div>
                <strong>International guests</strong>
                <p>Depending on the system, the same menu can be maintained in multiple languages.</p>
              </div>
            </div>
            <p>
              For concrete workflows for ongoing maintenance, we have a separate page:{" "}
              <Link to="/en/manage-menu-online-for-restaurants">manage a menu online</Link>.
            </p>
          </div>
        </section>

        <section id="bestplace" className="acquaintance">
          <h2>Digital menu with Best Place</h2>
          <p>
            Best Place is designed for restaurants that want to manage their menu
            online and make it accessible to guests on mobile devices. Categories,
            products, prices, descriptions and images are edited in the admin area
            and then shown on the public menu page.
          </p>
          <div className="qrArticle__cardGrid">
            <div>
              <strong>Menu management</strong>
              <p>Manage categories, dishes, prices and descriptions centrally.</p>
            </div>
            <div>
              <strong>Multiple languages</strong>
              <p>Manage language versions for international guests.</p>
            </div>
            <div>
              <strong>Mobile guest view</strong>
              <p>Open the menu in the smartphone browser.</p>
            </div>
            <div>
              <strong>Additional modules</strong>
              <p>Reservations and other features can be added depending on your needs.</p>
            </div>
          </div>
        </section>

        <section id="qr" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>When does a digital menu need a QR code?</h2>
            <p>
              A QR code is especially practical when guests should open the menu
              directly at the table, at the counter or from takeaway material. But
              the QR code is not the digital menu itself; it is only one way to
              access it.
            </p>
            <p>
              If your focus is QR access — creation, scanning, stable URL and use at
              the table — go to our{" "}
              <Link to="/en/qr-code-menu-for-restaurants">QR code menu for restaurants</Link> page.
            </p>
          </div>
        </section>

        <section id="faq" className="acquaintance">
          <h2>Digital menu FAQ</h2>
          <div className="seoFaq">
            <details>
              <summary>Is a digital menu the same as a QR menu?</summary>
              <p>
                Not exactly. The digital menu is the online-managed menu itself. A
                QR code is one way to open that menu.
              </p>
            </details>
            <details>
              <summary>Does a digital menu need an app?</summary>
              <p>
                No. A good mobile web menu can run directly in the browser.
              </p>
            </details>
            <details>
              <summary>Can I change prices at any time?</summary>
              <p>
                With a manageable digital menu, prices and products should be
                updateable without creating a new PDF file or reprinting the menu.
              </p>
            </details>
            <details>
              <summary>Can I offer multiple languages?</summary>
              <p>
                Yes, if the system you use supports multiple language versions.
              </p>
            </details>
            <details>
              <summary>Can I test a digital menu for free?</summary>
              <p>
                A test is useful for checking menu management and the guest view
                before making a long-term decision. Read more on our{" "}
                <Link to="/en/free-digital-menu-test">free digital menu test</Link> page.
              </p>
            </details>
          </div>
        </section>

        <section className="articls qrArticle__related">
          <h2>Related topics</h2>
          <div className="seoCompareSmall">
            <p><Link to="/en/qr-code-menu-for-restaurants">QR code menu for restaurants</Link></p>
            <p><Link to="/en/digital-menu-comparison">Compare digital menus</Link></p>
            <p><Link to="/en/multilingual-menu-for-restaurants">Multilingual menu</Link></p>
            <p><Link to="/en/manage-menu-online-for-restaurants">Manage a menu online</Link></p>
          </div>
        </section>
      </article>
    </main>
  );
}
